import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Users, Shield, ArrowLeft, UserPlus, UserMinus, CheckCircle, 
  Clock, AlertCircle, FileText, DollarSign, ChevronDown, ChevronUp,
  MoreVertical, X, Heart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminTeamDetail = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [showAddTeamMom, setShowAddTeamMom] = useState(false);
  const [teamMomUsers, setTeamMomUsers] = useState([]);
  const [teamMomSearch, setTeamMomSearch] = useState('');

  // Coach assignment state — mirrors Team Mom pattern (3 distinct staff slots).
  const [showAddHeadCoach, setShowAddHeadCoach] = useState(false);
  const [showAddAssistant, setShowAddAssistant] = useState(false);
  const [coachCandidates, setCoachCandidates] = useState([]);
  const [coachSearch, setCoachSearch] = useState('');

  // Play-Up One Level (super_admin only) — confirm modal state.
  const [playUpCandidate, setPlayUpCandidate] = useState(null); // candidate dict
  const [playUpReason, setPlayUpReason] = useState('');
  const [playingUp, setPlayingUp] = useState(false);
  const [playUpError, setPlayUpError] = useState('');
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchTeamDetail();
  }, [teamId]);

  const fetchTeamDetail = async () => {
    try {
      // Get full team detail (includes coaches, team moms, waitlist)
      const detailRes = await axios.get(`${API_URL}/api/teams/${teamId}/detail`, {
        withCredentials: true
      });
      
      // Get roster data via coach endpoint
      const rosterRes = await axios.get(`${API_URL}/api/coach/team/${teamId}/roster`, {
        withCredentials: true
      }).catch(() => ({ data: { roster: [], roster_count: 0 } }));
      
      // Get division info
      const divisionsRes = await axios.get(`${API_URL}/api/divisions`, {
        withCredentials: true
      });
      
      const division = divisionsRes.data.find(d => d.division_id === detailRes.data.division_id);
      
      const enrichedTeam = {
        ...detailRes.data,
        division,
        roster: rosterRes.data.roster?.map(p => ({
          ...p,
          eligibility: { 
            label: p.clearance_status === 'cleared' ? 'cleared' : 'pending',
            can_participate: p.clearance_status === 'cleared',
            blockers: []
          }
        })) || [],
        roster_count: rosterRes.data.roster_count || 0,
        waitlist: detailRes.data?.waitlist || [],
        waitlist_count: detailRes.data?.waitlist_count || 0,
        unassigned_eligible: detailRes.data?.unassigned_eligible || [],
        coaches: detailRes.data?.coaches || [],
        team_moms_detail: detailRes.data?.team_moms_detail || []
      };
      
      setTeam(enrichedTeam);
    } catch (error) {
      console.error('Failed to fetch team:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignPlayer = async (playerId) => {
    setAssigning(true);
    try {
      await axios.post(`${API_URL}/api/roster/assign`, {
        player_id: playerId,
        team_id: teamId
      }, { withCredentials: true });
      fetchTeamDetail();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to assign player');
    } finally {
      setAssigning(false);
    }
  };

  const handlePlayUpConfirm = async () => {
    if (!playUpCandidate || playingUp) return;
    setPlayingUp(true);
    setPlayUpError('');
    try {
      await axios.post(`${API_URL}/api/roster/assign`, {
        player_id: playUpCandidate.player_id,
        team_id: teamId,
        play_up_one_level: true,
        play_up_reason: playUpReason.trim() || undefined,
      }, { withCredentials: true });
      setPlayUpCandidate(null);
      setPlayUpReason('');
      fetchTeamDetail();
    } catch (error) {
      setPlayUpError(error?.response?.data?.detail || 'Failed to assign play-up.');
    } finally {
      setPlayingUp(false);
    }
  };

  const handleRemovePlayer = async (playerId) => {
    if (!confirm('Remove this player from the roster?')) return;
    try {
      await axios.post(`${API_URL}/api/roster/remove?player_id=${playerId}`, {}, {
        withCredentials: true
      });
      fetchTeamDetail();
    } catch (error) {
      alert('Failed to remove player');
    }
  };

  const handlePromoteFromWaitlist = async (waitlistId) => {
    try {
      await axios.post(`${API_URL}/api/waitlist/${waitlistId}/promote?team_id=${teamId}`, {}, {
        withCredentials: true
      });
      fetchTeamDetail();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to promote player');
    }
  };

  const fetchTeamMomCandidates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/users?role=team_mom`, { withCredentials: true });
      // Also include parents who could be assigned
      const parentRes = await axios.get(`${API_URL}/api/admin/users?role=parent`, { withCredentials: true });
      const allCandidates = [...(res.data.users || []), ...(parentRes.data.users || [])];
      // Filter out already assigned team moms
      const currentMomIds = (team?.team_moms || []);
      setTeamMomUsers(allCandidates.filter(u => !currentMomIds.includes(u.user_id)));
    } catch (error) {
      console.error('Failed to fetch team mom candidates:', error);
    }
  };

  const handleAddTeamMom = async (userId) => {
    const currentMoms = team?.team_moms || [];
    if (currentMoms.length >= 3) {
      alert('Maximum 3 Team Moms per team');
      return;
    }
    try {
      await axios.put(`${API_URL}/api/teams/${teamId}`, {
        team_moms: [...currentMoms, userId]
      }, { withCredentials: true });
      setShowAddTeamMom(false);
      setTeamMomSearch('');
      fetchTeamDetail();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add Team Mom');
    }
  };

  const handleRemoveTeamMom = async (userId) => {
    if (!window.confirm('Remove this Team Mom from the team?')) return;
    const currentMoms = team?.team_moms || [];
    try {
      await axios.put(`${API_URL}/api/teams/${teamId}`, {
        team_moms: currentMoms.filter(id => id !== userId)
      }, { withCredentials: true });
      fetchTeamDetail();
    } catch (error) {
      alert('Failed to remove Team Mom');
    }
  };

  // Coach pickers — fetch users with coach or team_staff role only.
  // Plain parents must first be promoted via Admin Users → Change Role
  // (same prerequisite the Team Mom flow has had since launch).
  const fetchCoachCandidates = async () => {
    try {
      const [coachRes, staffRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users?role=coach`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/users?role=team_staff`, { withCredentials: true }),
      ]);
      const merged = [...(coachRes.data.users || []), ...(staffRes.data.users || [])];
      // Dedupe by user_id (a user could theoretically match both queries; not today, but safe).
      const seen = new Set();
      const unique = merged.filter(u => {
        if (seen.has(u.user_id)) return false;
        seen.add(u.user_id);
        return true;
      });
      // Exclude already-assigned head coach + assistants so the same person
      // can't occupy two coach slots simultaneously.
      const taken = new Set([
        team?.head_coach_id,
        ...(team?.assistant_coaches || []),
      ].filter(Boolean));
      setCoachCandidates(unique.filter(u => !taken.has(u.user_id)));
    } catch (error) {
      console.error('Failed to fetch coach candidates:', error);
    }
  };

  const handleSetHeadCoach = async (userId) => {
    try {
      await axios.put(`${API_URL}/api/teams/${teamId}`, {
        head_coach_id: userId
      }, { withCredentials: true });
      setShowAddHeadCoach(false);
      setCoachSearch('');
      fetchTeamDetail();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to set Head Coach');
    }
  };

  const handleClearHeadCoach = async () => {
    if (!window.confirm('Remove the Head Coach from this team?')) return;
    try {
      await axios.put(`${API_URL}/api/teams/${teamId}`, {
        head_coach_id: null
      }, { withCredentials: true });
      fetchTeamDetail();
    } catch (error) {
      alert('Failed to remove Head Coach');
    }
  };

  const handleAddAssistant = async (userId) => {
    const current = team?.assistant_coaches || [];
    try {
      await axios.put(`${API_URL}/api/teams/${teamId}`, {
        assistant_coaches: [...current, userId]
      }, { withCredentials: true });
      setShowAddAssistant(false);
      setCoachSearch('');
      fetchTeamDetail();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add Assistant Coach');
    }
  };

  const handleRemoveAssistant = async (userId) => {
    if (!window.confirm('Remove this Assistant Coach from the team?')) return;
    const current = team?.assistant_coaches || [];
    try {
      await axios.put(`${API_URL}/api/teams/${teamId}`, {
        assistant_coaches: current.filter(id => id !== userId)
      }, { withCredentials: true });
      fetchTeamDetail();
    } catch (error) {
      alert('Failed to remove Assistant Coach');
    }
  };

  const getEligibilityBadge = (eligibility) => {
    const label = eligibility?.label || 'unknown';
    const colors = {
      cleared: 'bg-green-100 text-green-800',
      override_approved: 'bg-blue-100 text-blue-800',
      pending_docs: 'bg-yellow-100 text-yellow-800',
      pending_payment: 'bg-orange-100 text-orange-800',
      pending_verification: 'bg-purple-100 text-purple-800',
      pending_waiver: 'bg-pink-100 text-pink-800',
      registration_incomplete: 'bg-gray-100 text-gray-800',
      not_eligible: 'bg-red-100 text-red-800',
      waitlisted: 'bg-blue-100 text-blue-800'
    };
    return colors[label] || 'bg-gray-100 text-gray-800';
  };

  const formatEligibilityLabel = (label) => {
    return label?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-gray-200 rounded-lg w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  if (!team) {
    return (
      <ParentLayout>
        <div className="max-w-7xl mx-auto px-4 py-8 text-center">
          <p className="text-gray-500">Team not found</p>
          <Button onClick={() => navigate('/admin/teams')} className="mt-4">
            Back to Teams
          </Button>
        </div>
      </ParentLayout>
    );
  }

  const capacityPercent = (team.roster_count / team.roster_capacity) * 100;

  return (
    <ParentLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-team-detail">
        {/* Back Button */}
        <button 
          onClick={() => navigate('/admin/teams')}
          className="flex items-center text-gray-600 hover:text-navy mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Teams
        </button>

        {/* Team Header */}
        <div className="bg-gradient-to-r from-navy to-navy/90 rounded-xl p-6 mb-6 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center space-x-4">
              {team.logo_url ? (
                <div className="w-20 h-20 flex items-center justify-center flex-shrink-0">
                  <img 
                    src={team.logo_url} 
                    alt={`${team.name} logo`}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ) : (
                <div className="w-16 h-16 bg-gold/20 rounded-xl flex items-center justify-center">
                  <Shield className="w-8 h-8 text-gold" />
                </div>
              )}
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">{team.name}</h1>
                <p className="text-light-blue">{team.division?.name || team.division_name}</p>
              </div>
            </div>
            <div className="mt-4 sm:mt-0 flex items-center space-x-4">
              <div className="text-center">
                <p className="text-3xl font-bold">{team.roster_count}</p>
                <p className="text-sm text-white/70">of {team.roster_limit || team.roster_capacity}</p>
              </div>
              <div className="w-24 h-3 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full ${capacityPercent >= 100 ? 'bg-red' : capacityPercent >= 80 ? 'bg-yellow-400' : 'bg-green-400'}`}
                  style={{ width: `${Math.min(capacityPercent, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Coaches & Team Moms */}
        <Card className="mb-6">
          <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-navy text-lg">Coaching Staff & Team Moms</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                className="bg-navy hover:bg-navy/90 text-white"
                onClick={() => { setShowAddHeadCoach(true); setShowAddAssistant(false); setShowAddTeamMom(false); fetchCoachCandidates(); }}
                data-testid={team.head_coach_id ? 'change-head-coach-btn' : 'set-head-coach-btn'}
              >
                <Shield className="w-3.5 h-3.5 mr-1" />
                {team.head_coach_id ? 'Change Head Coach' : 'Set Head Coach'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-navy text-navy hover:bg-navy/5"
                onClick={() => { setShowAddAssistant(true); setShowAddHeadCoach(false); setShowAddTeamMom(false); fetchCoachCandidates(); }}
                data-testid="add-assistant-btn"
              >
                <UserPlus className="w-3.5 h-3.5 mr-1" />
                Assistant Coach
              </Button>
              {(team.team_moms || []).length < 3 && (
                <Button
                  size="sm"
                  className="bg-pink-500 hover:bg-pink-600 text-white"
                  onClick={() => { setShowAddTeamMom(true); setShowAddHeadCoach(false); setShowAddAssistant(false); fetchTeamMomCandidates(); }}
                  data-testid="add-team-mom-btn"
                >
                  <Heart className="w-3.5 h-3.5 mr-1" />
                  Add Team Mom
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {team.coaches?.map((coach, idx) => {
                const isHead = coach.coach_role === 'Head Coach';
                return (
                  <div
                    key={`coach-${coach.user_id || idx}`}
                    className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg group"
                    data-testid={isHead ? 'head-coach-chip' : `assistant-chip-${coach.user_id}`}
                  >
                    <div className="w-8 h-8 bg-navy rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {coach.name?.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-sm text-navy">{coach.name}</p>
                      <p className="text-xs text-gray-500">{coach.coach_role}</p>
                    </div>
                    <button
                      onClick={() => isHead ? handleClearHeadCoach() : handleRemoveAssistant(coach.user_id)}
                      className="ml-1 text-gray-300 hover:text-red transition opacity-0 group-hover:opacity-100"
                      title={isHead ? 'Remove Head Coach' : 'Remove Assistant'}
                      data-testid={isHead ? 'remove-head-coach-btn' : `remove-assistant-${coach.user_id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
              {team.team_moms_detail?.map((mom, idx) => (
                <div key={`mom-${idx}`} className="flex items-center space-x-2 bg-pink-50 px-3 py-2 rounded-lg group">
                  <div className="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {mom.name?.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-navy">{mom.name}</p>
                    <p className="text-xs text-pink-600">Team Mom</p>
                  </div>
                  <button
                    onClick={() => handleRemoveTeamMom(mom.user_id)}
                    className="ml-1 text-gray-300 hover:text-red transition opacity-0 group-hover:opacity-100"
                    title="Remove"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {(!team.coaches?.length && !team.team_moms_detail?.length) && (
                <p className="text-sm text-gray-400">No staff assigned</p>
              )}
            </div>

            {/* Add Team Mom Modal */}
            {showAddTeamMom && (
              <div className="mt-4 border border-pink-200 rounded-lg p-4 bg-pink-50/50">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-navy text-sm">Add Team Mom ({(team.team_moms || []).length}/3)</p>
                  <button onClick={() => setShowAddTeamMom(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={teamMomSearch}
                  onChange={(e) => setTeamMomSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2"
                  data-testid="team-mom-search"
                />
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {teamMomUsers
                    .filter(u => !teamMomSearch || u.name?.toLowerCase().includes(teamMomSearch.toLowerCase()))
                    .slice(0, 10)
                    .map(u => (
                      <button
                        key={u.user_id}
                        onClick={() => handleAddTeamMom(u.user_id)}
                        className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-pink-100 transition text-left"
                        data-testid={`add-mom-${u.user_id}`}
                      >
                        <div className="w-7 h-7 bg-pink-200 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-pink-700 text-xs font-bold">
                            {u.name?.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-navy">{u.name}</p>
                          <p className="text-xs text-gray-500">{u.email} <Badge className="text-xs ml-1">{u.role}</Badge></p>
                        </div>
                      </button>
                    ))}
                  {teamMomUsers.filter(u => !teamMomSearch || u.name?.toLowerCase().includes(teamMomSearch.toLowerCase())).length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-3">No users found. Assign the "Team Mom" role first in Admin Users.</p>
                  )}
                </div>
              </div>
            )}

            {/* Set/Change Head Coach Modal */}
            {showAddHeadCoach && (
              <div className="mt-4 border border-navy/20 rounded-lg p-4 bg-navy/5">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-navy text-sm">
                    {team.head_coach_id ? 'Change Head Coach' : 'Set Head Coach'}
                  </p>
                  <button
                    onClick={() => { setShowAddHeadCoach(false); setCoachSearch(''); }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={coachSearch}
                  onChange={(e) => setCoachSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2"
                  data-testid="head-coach-search"
                />
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {coachCandidates
                    .filter(u => !coachSearch || u.name?.toLowerCase().includes(coachSearch.toLowerCase()))
                    .slice(0, 10)
                    .map(u => (
                      <button
                        key={u.user_id}
                        onClick={() => handleSetHeadCoach(u.user_id)}
                        className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-navy/10 transition text-left"
                        data-testid={`set-head-coach-${u.user_id}`}
                      >
                        <div className="w-7 h-7 bg-navy/15 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-navy text-xs font-bold">
                            {u.name?.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-navy">{u.name}</p>
                          <p className="text-xs text-gray-500">{u.email} <Badge className="text-xs ml-1">{u.role}</Badge></p>
                        </div>
                      </button>
                    ))}
                  {coachCandidates.filter(u => !coachSearch || u.name?.toLowerCase().includes(coachSearch.toLowerCase())).length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-3">No eligible users. Assign the "Coach" or "Team Staff" role first in Admin Users.</p>
                  )}
                </div>
              </div>
            )}

            {/* Add Assistant Coach Modal */}
            {showAddAssistant && (
              <div className="mt-4 border border-navy/20 rounded-lg p-4 bg-navy/5">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-navy text-sm">Add Assistant Coach</p>
                  <button
                    onClick={() => { setShowAddAssistant(false); setCoachSearch(''); }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={coachSearch}
                  onChange={(e) => setCoachSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2"
                  data-testid="assistant-search"
                />
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {coachCandidates
                    .filter(u => !coachSearch || u.name?.toLowerCase().includes(coachSearch.toLowerCase()))
                    .slice(0, 10)
                    .map(u => (
                      <button
                        key={u.user_id}
                        onClick={() => handleAddAssistant(u.user_id)}
                        className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-navy/10 transition text-left"
                        data-testid={`add-assistant-${u.user_id}`}
                      >
                        <div className="w-7 h-7 bg-navy/15 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-navy text-xs font-bold">
                            {u.name?.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-navy">{u.name}</p>
                          <p className="text-xs text-gray-500">{u.email} <Badge className="text-xs ml-1">{u.role}</Badge></p>
                        </div>
                      </button>
                    ))}
                  {coachCandidates.filter(u => !coachSearch || u.name?.toLowerCase().includes(coachSearch.toLowerCase())).length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-3">No eligible users. Assign the "Coach" or "Team Staff" role first in Admin Users.</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Active Roster */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-navy">Active Roster ({team.roster?.length || 0})</CardTitle>
                <Button 
                  size="sm" 
                  onClick={() => setShowUnassigned(!showUnassigned)}
                  className="bg-navy hover:bg-navy/90"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Player
                </Button>
              </CardHeader>
              <CardContent>
                {team.roster?.length > 0 ? (
                  <div className="space-y-3">
                    {team.roster.map(player => (
                      <div 
                        key={player.player_id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                        data-testid={`roster-player-${player.player_id}`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-navy rounded-full flex items-center justify-center text-white font-bold">
                            {player.assignment?.jersey_number || player.jersey_number || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-navy">
                              {player.first_name} {player.last_name}
                            </p>
                            <div className="flex items-center space-x-2 text-xs text-gray-600">
                              <span>{player.age_group}</span>
                              <span>•</span>
                              <span>{player.assignment?.position || player.position || 'TBD'}</span>
                              {(player.assignment?.age_group_override || player.plays_up) && (
                                <>
                                  <span>•</span>
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[10px] font-semibold uppercase tracking-wide"
                                    title={
                                      player.assignment?.age_group_override_reason
                                        ? `Plays up — One level. Reason: ${player.assignment.age_group_override_reason}`
                                        : 'Plays up — One level (super-admin override)'
                                    }
                                    data-testid={`plays-up-badge-${player.player_id}`}
                                  >
                                    Plays Up · One Level
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Badge className={getEligibilityBadge(player.eligibility)}>
                            {formatEligibilityLabel(player.eligibility?.label)}
                          </Badge>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemovePlayer(player.player_id);
                            }}
                            className="p-1 hover:bg-red/10 rounded text-red"
                            title="Remove from roster"
                          >
                            <UserMinus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No players assigned</p>
                  </div>
                )}

                {/* Blockers Summary */}
                {team.roster?.some(p => p.eligibility?.blockers?.length > 0) && (
                  <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                    <p className="font-medium text-yellow-800 mb-2">Players with Blockers:</p>
                    {team.roster.filter(p => p.eligibility?.blockers?.length > 0).map(player => (
                      <div key={player.player_id} className="text-sm text-yellow-700 mb-1">
                        <span className="font-medium">{player.first_name} {player.last_name}:</span>
                        {' '}{player.eligibility.blockers.join(', ')}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Unassigned Eligible Players */}
            {showUnassigned && (
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="text-navy">Available Players</CardTitle>
                </CardHeader>
                <CardContent>
                  {team.unassigned_eligible?.length > 0 ? (
                    <div className="space-y-2">
                      {team.unassigned_eligible.map(player => {
                        const blockers = player.eligibility?.blockers || [];
                        return (
                          <div
                            key={player.player_id}
                            className="flex items-center justify-between p-3 border rounded-lg"
                            data-testid={`available-player-${player.player_id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-navy">
                                {player.first_name} {player.last_name}
                              </p>
                              <div className="flex items-center flex-wrap gap-2 mt-1">
                                <span className="text-xs text-gray-600">{player.age_group}</span>
                                <Badge
                                  className={`text-xs ${getEligibilityBadge(player.eligibility)}`}
                                  data-testid={`available-player-status-${player.player_id}`}
                                >
                                  {formatEligibilityLabel(player.eligibility?.label)}
                                </Badge>
                              </div>
                              {blockers.length > 0 && (
                                <p className="text-xs text-gray-500 mt-1 truncate" title={blockers.join(', ')} data-testid={`available-player-blockers-${player.player_id}`}>
                                  {blockers.join(', ')}
                                </p>
                              )}
                            </div>
                            <div className="ml-3 flex-shrink-0">
                              <Button
                                size="sm"
                                onClick={() => handleAssignPlayer(player.player_id)}
                                disabled={assigning || team.roster_count >= team.roster_capacity}
                                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                data-testid={`assign-player-${player.player_id}`}
                              >
                                <UserPlus className="w-4 h-4 mr-1" />
                                Assign
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-4">No unassigned players in this division</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Play Up Override — Super Admin only */}
            {isSuperAdmin && (team.play_up_candidates?.length || 0) > 0 && (
              <Card className="mt-4 border-purple-200" data-testid="play-up-panel">
                <CardHeader>
                  <CardTitle className="text-navy flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-purple-700" />
                    Play Up Override
                    <span className="text-[10px] uppercase tracking-wider bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded">
                      Super Admin
                    </span>
                  </CardTitle>
                  <p className="text-xs text-gray-500 mt-1">
                    One level up only — players from the immediately-lower division on the same program ladder. Assignment is logged as an override.
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {team.play_up_candidates.map((c) => (
                      <div
                        key={c.player_id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                        data-testid={`play-up-candidate-${c.player_id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-navy">
                            {c.first_name} {c.last_name}
                          </p>
                          <div className="flex items-center flex-wrap gap-2 mt-1 text-xs text-gray-600">
                            <span>Currently {c.age_group || c.from_division_id}</span>
                            <span>·</span>
                            <span>Will play up to this team</span>
                          </div>
                        </div>
                        <div className="ml-3 flex-shrink-0">
                          <Button
                            size="sm"
                            onClick={() => {
                              setPlayUpCandidate(c);
                              setPlayUpReason('');
                              setPlayUpError('');
                            }}
                            disabled={team.roster_count >= team.roster_capacity}
                            className="bg-purple-700 hover:bg-purple-800 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            data-testid={`play-up-btn-${c.player_id}`}
                          >
                            Assign One Level Up
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Play Up Confirm Modal — Super Admin only */}
            {isSuperAdmin && playUpCandidate && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="play-up-confirm-modal">
                <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
                  <h3 className="text-lg font-bold text-navy mb-1">Confirm Play-Up Override</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    This will roster <span className="font-semibold">{playUpCandidate.first_name} {playUpCandidate.last_name}</span> ({playUpCandidate.age_group || 'one level below'}) onto <span className="font-semibold">{team.name}</span> as a one-level-up exception. The override will be logged on the player's record.
                  </p>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">
                    Reason (optional)
                  </label>
                  <textarea
                    value={playUpReason}
                    onChange={(e) => setPlayUpReason(e.target.value.slice(0, 200))}
                    rows={3}
                    placeholder="e.g., Skill-tested up — strong fundamentals; coach + parent approved."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent text-sm"
                    data-testid="play-up-reason"
                  />
                  <p className="text-[11px] text-gray-400 mt-0.5">{playUpReason.length}/200</p>
                  {playUpError && (
                    <p className="text-sm text-red-700 mt-2" data-testid="play-up-error">{playUpError}</p>
                  )}
                  <div className="flex justify-end gap-2 mt-4">
                    <Button
                      variant="outline"
                      onClick={() => { setPlayUpCandidate(null); setPlayUpReason(''); setPlayUpError(''); }}
                      disabled={playingUp}
                      data-testid="play-up-cancel"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handlePlayUpConfirm}
                      disabled={playingUp}
                      className="bg-purple-700 hover:bg-purple-800 text-white"
                      data-testid="play-up-confirm"
                    >
                      {playingUp ? 'Assigning…' : 'Confirm One Level Up'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Waitlist */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="text-navy flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  Waitlist ({team.waitlist?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {team.waitlist?.length > 0 ? (
                  <div className="space-y-3">
                    {team.waitlist.map((entry, idx) => (
                      <div 
                        key={entry.waitlist_id}
                        className="p-3 border rounded-lg"
                        data-testid={`waitlist-entry-${entry.waitlist_id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs font-bold">
                              {entry.priority_order}
                            </span>
                            <span className="font-medium text-navy">
                              {entry.player?.first_name} {entry.player?.last_name}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <Badge className={getEligibilityBadge(entry.eligibility)} variant="secondary">
                            {formatEligibilityLabel(entry.eligibility?.label)}
                          </Badge>
                          {team.roster_count < team.roster_capacity && (
                            <Button
                              size="sm"
                              onClick={() => handlePromoteFromWaitlist(entry.waitlist_id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              Promote
                            </Button>
                          )}
                        </div>
                        {entry.eligibility?.blockers?.length > 0 && (
                          <div className="mt-2 text-xs text-red">
                            {entry.eligibility.blockers.join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No players waitlisted</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Team Info */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-navy text-lg">Team Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Jersey Color</span>
                  <span className="font-medium">{team.jersey_color || 'Not set'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Practice Group</span>
                  <span className="font-medium">{team.practice_group || 'Not set'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status</span>
                  <Badge className={team.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100'}>
                    {team.status}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </ParentLayout>
  );
};

export default AdminTeamDetail;
